/**
 * src/components/shared/Workbench.jsx
 *
 * Generic configurable list view used across the app — Documents, Risks,
 * Vendors, Employees, Threats, Tasks, Vulnerabilities, Controls.
 *
 * Capabilities (see docs/workbench.md for the full spec):
 *   - Default + optional columns via picker (persisted per user)
 *   - Per-column filters (text / select / multi / range / boolean / custom)
 *   - Sort, density toggle (compact / comfortable), global search, reset
 *   - Drag-and-drop group-by (multi-level, collapse/expand, per-group counts)
 *   - Saved views (per user, localStorage)
 *   - Row click → consumer-defined handler
 *   - Bulk select + bulk actions declared by the consumer
 *   - Optional view-mode toggle (table ↔ existing rich view)
 *
 * The component is purely a view layer — it never writes state directly.
 * Bulk updates are routed back through the consumer's `onBulkUpdate(ids, patch)`,
 * which is responsible for whitelisting allowed patch keys to preserve module
 * boundaries.
 */

import { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import {
  computeFittedColumnWidths,
  DEFAULT_COL_WIDTH,
  MIN_COL_WIDTH,
  MAX_COL_WIDTH,
} from './workbenchLayout.js';
import { orderRowsNewestFirst } from './workbenchOrder.js';

// The row-select checkbox column. Wide enough for the checkbox plus the cell
// padding, and pinned there — it must never be squeezed out of existence.
const SELECT_COL_WIDTH = 34;

const PREFS_VERSION = 1;
const PREFS_KEY = (storageKey, uid) =>
  `seasuite_workbench_v${PREFS_VERSION}_${storageKey}_${uid || 'anon'}`;

// Column widths are computed to fit the measured container rather than derived
// from cell content — see workbenchLayout.js for the arithmetic and why. A
// column that declares no `size` starts at DEFAULT_COL_WIDTH and is the first
// to give ground when space is short; MIN/MAX bound every column unless the
// consumer declares its own `minSize` / `maxSize`.

/* ── localStorage helpers ──────────────────────────────────────────────── */
function loadPrefs(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? parsed : {};
  } catch {
    return {};
  }
}
function savePrefs(key, prefs) {
  try {
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    /* quota / disabled */
  }
}

/* ── Filter helpers ────────────────────────────────────────────────────── */
const arrIncludesSomeFilter = (row, columnId, filterValue) => {
  if (!filterValue || !filterValue.length) return true;
  const cell = row.getValue(columnId);
  if (cell == null) return false;
  const cellArr = Array.isArray(cell) ? cell : [cell];
  const cellIds = cellArr.map((v) => (typeof v === 'object' ? (v.id ?? v.name) : v));
  return filterValue.some((v) => cellIds.includes(v));
};

const rangeFilter = (row, columnId, filterValue) => {
  const v = Number(row.getValue(columnId));
  if (Number.isNaN(v)) return false;
  const [min, max] = filterValue || ['', ''];
  if (min !== '' && v < Number(min)) return false;
  if (max !== '' && v > Number(max)) return false;
  return true;
};

const booleanFilter = (row, columnId, filterValue) => {
  if (!filterValue || filterValue === 'all') return true;
  const cell = row.getValue(columnId);
  return filterValue === 'yes' ? !!cell : !cell;
};

/**
 * Every key this component reads off a consumer column definition.
 *
 * It exists so an unrecognised one can be reported instead of ignored
 * (GH #6741). The findings register carried `hidden: true` on three columns
 * for the life of the module: a plausible-looking key that this file has never
 * read, so the layout those columns described never shipped and nothing —
 * not a lint rule, not a type, not a test, not a review — said a word. A
 * config key that silently does nothing is indistinguishable from one that
 * works until somebody opens the screen and counts columns.
 *
 * Derived from what `adaptColumn` and the visibility/label maps actually
 * consume, not from the doc comment, which had drifted: it listed eleven keys
 * while the code read fifteen, so `minSize`, `maxSize`, `sortingFn` and `meta`
 * were all in live use and undocumented. A list maintained by hand beside the
 * code that uses it is the same failure one level up, so keep this in step
 * with `adaptColumn` — the dev-mode check below is what makes drift audible.
 */
const COLUMN_SPEC_KEYS = new Set([
  'id',
  'label',
  'accessorKey',
  'accessorFn',
  'cell',
  'filter',
  'sortable',
  'sortingFn',
  'groupable',
  'getGroupingValue',
  'defaultVisible',
  'size',
  'minSize',
  'maxSize',
  'meta',
]);

/**
 * Say so when a column definition carries a key nothing reads.
 *
 * Deliberately a DEV-ONLY console error rather than a throw. Tryggr is live:
 * a hard failure here would take a whole register off the screen for a
 * customer over a typo that, by definition, changes nothing about how the
 * register behaves. The person who can act on this is the one writing the
 * column file, and they are the one running the dev server.
 */
function warnOnUnknownColumnKeys(def) {
  if (!import.meta.env?.DEV) return;
  const unknown = Object.keys(def || {}).filter((k) => !COLUMN_SPEC_KEYS.has(k));
  if (unknown.length === 0) return;
  console.error(
    `[Workbench] column "${def?.id}" carries ${unknown.length} key(s) the Workbench does not ` +
      `read: ${unknown.join(', ')}. They do nothing. If you meant to hide this column by ` +
      'default the key is `defaultVisible: false` — `hidden` is not read. See GH issue 6741.'
  );
}

/**
 * Adapt a consumer-supplied column definition to a TanStack column.
 * Consumer shape (see docs/workbench.md) — the authoritative list is
 * COLUMN_SPEC_KEYS above:
 *   { id, label, accessorKey?, accessorFn?, sortable?, sortingFn?, groupable?,
 *     cell?, filter?: { kind, options?, derivedFrom?, customFn? },
 *     defaultVisible?, getGroupingValue?, size?, minSize?, maxSize?, meta? }
 */
function adaptColumn(def, density) {
  warnOnUnknownColumnKeys(def);
  const pad = density === 'compact' ? '4px 8px' : '8px 10px';
  const out = {
    id: def.id,
    header: def.label,
    cell: def.cell || (({ getValue }) => getValue() ?? '—'),
    enableSorting: def.sortable !== false,
    enableGrouping: def.groupable === true,
    enableColumnFilter: !!def.filter,
    meta: { ...(def.meta || {}), filter: def.filter, pad, label: def.label },
  };
  // Only set `size` when the consumer actually declared one — a column with no
  // declared size is the flexible kind that absorbs surplus width and gives it
  // back first when the container is narrow (#5043). `minSize` is how a column
  // whose content cannot usefully truncate — a row-actions column of buttons —
  // declares that it must not be squeezed.
  if (def.size != null) out.size = def.size;
  if (def.minSize != null) out.minSize = def.minSize;
  if (def.maxSize != null) out.maxSize = def.maxSize;
  if (def.accessorKey) out.accessorKey = def.accessorKey;
  if (def.accessorFn) out.accessorFn = def.accessorFn;
  if (def.getGroupingValue) out.getGroupingValue = def.getGroupingValue;
  if (def.sortingFn) out.sortingFn = def.sortingFn;

  // Filter wiring
  if (def.filter) {
    if (def.filter.kind === 'multi') out.filterFn = arrIncludesSomeFilter;
    if (def.filter.kind === 'range') out.filterFn = rangeFilter;
    if (def.filter.kind === 'boolean') out.filterFn = booleanFilter;
    if (def.filter.kind === 'custom') out.filterFn = def.filter.customFn;
  }
  return out;
}

/* ── Cell helpers exported for consumers ───────────────────────────────── */
export function ChipsCell({
  items,
  getLabel = (x) => (typeof x === 'object' ? (x.name ?? x.id) : x),
  max = 3,
  accent,
}) {
  const arr = items || [];
  if (!arr.length) return <span style={{ color: 'var(--fg3)' }}>—</span>;
  const shown = arr.slice(0, max);
  const extra = arr.length - shown.length;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {shown.map((x, i) => (
        <span key={i} className={accent ? 'chip chip-accent' : 'chip'} style={{ fontSize: 9 }}>
          {getLabel(x)}
        </span>
      ))}
      {extra > 0 && (
        <span className="chip" style={{ fontSize: 9, color: 'var(--fg3)' }}>
          +{extra}
        </span>
      )}
    </div>
  );
}

/* A column filter counts as "active" only when its value would actually
   exclude rows: a cleared text box is '', a cleared range is ['', ''], and a
   select back on its "all" option is either undefined or the string 'all'.
   Anything else marks the column in the header. */
function isActiveFilterValue(v) {
  if (v == null) return false;
  if (Array.isArray(v)) return v.some((x) => x !== '' && x != null);
  if (typeof v === 'string') return v.trim() !== '' && v !== 'all';
  return true;
}

/* Human-readable form of a column's filter value, for the header tooltip —
   option labels where the column declares them, raw values otherwise. */
function describeFilterValue(value, meta) {
  const filter = meta?.filter;
  const labelFor = (v) => {
    const opt = (filter?.options || []).find((o) => String(o.value) === String(v));
    return opt ? opt.label : String(v);
  };
  if (Array.isArray(value)) {
    if (filter?.kind === 'range') {
      const [min, max] = value;
      if (min !== '' && max !== '') return `${min} – ${max}`;
      return min !== '' ? `≥ ${min}` : `≤ ${max}`;
    }
    return value.map(labelFor).join(', ');
  }
  return labelFor(value);
}

function FilterFunnelIcon() {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 10 10"
      aria-hidden="true"
      focusable="false"
      style={{ flex: '0 0 auto', display: 'block' }}
    >
      <path d="M0.5 1.2h9L6 5.2v3.6L4 7.6V5.2z" fill="currentColor" />
    </svg>
  );
}

/* ── Per-column filter UI ──────────────────────────────────────────────── */
function ColumnFilter({ column, rows }) {
  const meta = column.columnDef.meta || {};
  const filter = meta.filter;
  if (!filter || column.columnDef.enableColumnFilter === false) {
    return <div style={{ height: 26 }} />;
  }
  const value = column.getFilterValue();

  if (filter.kind === 'text') {
    return (
      <input
        className="input"
        placeholder="Filter…"
        value={value || ''}
        onChange={(e) => column.setFilterValue(e.target.value || undefined)}
        style={{ width: '100%', minWidth: 0, fontSize: 11, padding: '3px 6px', height: 26 }}
      />
    );
  }
  if (filter.kind === 'select') {
    return (
      <select
        className="input"
        value={value || 'all'}
        onChange={(e) =>
          column.setFilterValue(e.target.value === 'all' ? undefined : e.target.value)
        }
        style={{ width: '100%', minWidth: 0, fontSize: 11, padding: '3px 6px', height: 26 }}
      >
        {(filter.options || []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  if (filter.kind === 'range') {
    const [min, max] = value || ['', ''];
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          className="input"
          type="number"
          placeholder="≥"
          value={min}
          onChange={(e) => column.setFilterValue([e.target.value, max])}
          style={{
            flex: '1 1 0',
            width: '100%',
            fontSize: 11,
            padding: '3px 6px',
            height: 26,
            minWidth: 0,
          }}
        />
        <input
          className="input"
          type="number"
          placeholder="≤"
          value={max}
          onChange={(e) => column.setFilterValue([min, e.target.value])}
          style={{
            flex: '1 1 0',
            width: '100%',
            fontSize: 11,
            padding: '3px 6px',
            height: 26,
            minWidth: 0,
          }}
        />
      </div>
    );
  }
  if (filter.kind === 'boolean') {
    return (
      <select
        className="input"
        value={value || 'all'}
        onChange={(e) =>
          column.setFilterValue(e.target.value === 'all' ? undefined : e.target.value)
        }
        style={{ width: '100%', minWidth: 0, fontSize: 11, padding: '3px 6px', height: 26 }}
      >
        <option value="all">All</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  }
  if (filter.kind === 'multi') {
    const options = computeMultiOptions(filter, rows, column.id);
    return (
      <MultiSelectFilter
        options={options}
        value={value || []}
        onChange={(v) => column.setFilterValue(v.length ? v : undefined)}
      />
    );
  }
  if (filter.kind === 'custom') {
    return (
      <select
        className="input"
        value={value || 'all'}
        onChange={(e) =>
          column.setFilterValue(e.target.value === 'all' ? undefined : e.target.value)
        }
        style={{ width: '100%', minWidth: 0, fontSize: 11, padding: '3px 6px', height: 26 }}
      >
        {(filter.options || []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  return null;
}

function computeMultiOptions(filter, rows, columnId) {
  if (filter.options) return filter.options;
  const path = filter.derivedFrom || columnId;
  const seen = new Map();
  for (const r of rows) {
    const v = r[path];
    if (v == null) continue;
    const arr = Array.isArray(v) ? v : [v];
    for (const item of arr) {
      const id = typeof item === 'object' ? (item.id ?? item.name) : item;
      const label = typeof item === 'object' ? (item.name ?? item.id) : item;
      if (id != null && !seen.has(id)) seen.set(id, label);
    }
  }
  return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
}

function MultiSelectFilter({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const toggle = (v) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };
  const summary = value.length === 0 ? 'Any' : `${value.length} selected`;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="input"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        style={{
          width: '100%',
          minWidth: 0,
          fontSize: 11,
          padding: '3px 6px',
          height: 26,
          textAlign: 'left',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {summary}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 30,
            marginTop: 4,
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 6,
            minWidth: 180,
            maxHeight: 240,
            overflow: 'auto',
            boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
          }}
        >
          {value.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => onChange([])}
                style={{ fontSize: 10 }}
              >
                Clear
              </button>
            </div>
          )}
          {options.length === 0 && (
            <div style={{ color: 'var(--fg3)', fontSize: 11, padding: 4 }}>No options</div>
          )}
          {options.map((o) => (
            <label
              key={o.value}
              style={{
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                padding: '3px 4px',
                fontSize: 11,
                color: 'var(--fg2)',
                cursor: 'pointer',
                borderRadius: 4,
              }}
            >
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {o.label}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Drag-and-drop group-by zone ───────────────────────────────────────── */
function GroupByZone({ grouping, onChange, columns, columnLabels, onExpandAll, onCollapseAll }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'group-by-zone' });
  const labelFor = (id) => columnLabels[id] || id;
  const remove = (id) => onChange(grouping.filter((g) => g !== id));
  const groupableCols = columns.filter((c) => c.columnDef.enableGrouping);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span
        style={{
          fontSize: 10,
          color: 'var(--fg3)',
          fontFamily: 'var(--font-mono)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        Group by:
      </span>
      <div
        ref={setNodeRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          flex: 1,
          minHeight: 32,
          padding: '4px 8px',
          border: `1px dashed ${isOver ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 6,
          background: isOver ? 'var(--accent-bg)' : 'transparent',
        }}
      >
        {grouping.length === 0 && (
          <span style={{ fontSize: 11, color: 'var(--fg3)', fontStyle: 'italic' }}>
            Drag a column header here, or pick one from the menu →
          </span>
        )}
        {grouping.map((g) => (
          <span
            key={g}
            className="chip chip-accent"
            style={{
              fontSize: 11,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 4px 2px 8px',
            }}
          >
            {labelFor(g)}
            <button
              onClick={() => remove(g)}
              aria-label={`Remove ${labelFor(g)} grouping`}
              style={{
                background: 'transparent',
                border: 0,
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 12,
                lineHeight: 1,
                padding: '0 2px',
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <details style={{ position: 'relative' }}>
        <summary
          className="btn btn-ghost btn-sm"
          style={{ fontSize: 11, cursor: 'pointer', listStyle: 'none' }}
        >
          + add
        </summary>
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 30,
            marginTop: 4,
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 6,
            minWidth: 160,
            boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
          }}
        >
          {groupableCols
            .filter((c) => !grouping.includes(c.id))
            .map((c) => (
              <button
                key={c.id}
                onClick={() => onChange([...grouping, c.id])}
                className="btn btn-ghost btn-sm"
                style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 11 }}
              >
                {labelFor(c.id)}
              </button>
            ))}
          {groupableCols.filter((c) => !grouping.includes(c.id)).length === 0 && (
            <div style={{ color: 'var(--fg3)', fontSize: 11, padding: 4 }}>All grouped</div>
          )}
        </div>
      </details>

      {/* Expand / collapse all groups — only meaningful when grouping is active */}
      {grouping.length > 0 && (
        <div
          style={{
            display: 'inline-flex',
            border: '1px solid var(--border)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <button
            onClick={() => onExpandAll?.()}
            className="btn btn-ghost btn-sm"
            title="Expand all groups"
            aria-label="Expand all groups"
            style={{ fontSize: 11, borderRadius: 0, border: 0 }}
          >
            ▼ Expand all
          </button>
          <button
            onClick={() => onCollapseAll?.()}
            className="btn btn-ghost btn-sm"
            title="Collapse all groups"
            aria-label="Collapse all groups"
            style={{ fontSize: 11, borderRadius: 0, border: 0 }}
          >
            ▶ Collapse all
          </button>
        </div>
      )}
    </div>
  );
}

function DraggableHeader({ column, children }) {
  const groupable = column.columnDef.enableGrouping;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `header-${column.id}`,
    data: { columnId: column.id },
    disabled: !groupable,
  });
  return (
    <div
      ref={setNodeRef}
      {...(groupable ? attributes : {})}
      {...(groupable ? listeners : {})}
      style={{
        cursor: isDragging ? 'grabbing' : 'inherit',
        opacity: isDragging ? 0.4 : 1,
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        minWidth: 0,
        overflow: 'hidden',
      }}
      title={groupable ? 'Drag to group by this column' : undefined}
    >
      {children}
    </div>
  );
}

/* ── Toolbar ───────────────────────────────────────────────────────────── */

const VIEWPORT_MARGIN = 8;

/**
 * Pull a right-anchored toolbar popup back inside the viewport. `right: 0`
 * on a trigger near the left edge (a wrapped toolbar on a phone) pushes a
 * 200px+ menu off-screen, where its left part cannot be seen or tapped.
 */
function keepInViewport(el) {
  if (!el || typeof window === 'undefined') return;
  el.style.transform = '';
  const r = el.getBoundingClientRect();
  if (!r.width) return;
  const vw = document.documentElement.clientWidth || window.innerWidth;
  let dx = 0;
  if (r.left < VIEWPORT_MARGIN) dx = VIEWPORT_MARGIN - r.left;
  else if (r.right > vw - VIEWPORT_MARGIN) dx = vw - VIEWPORT_MARGIN - r.right;
  if (dx) el.style.transform = `translateX(${Math.round(dx)}px)`;
}

function Toolbar({
  table,
  density,
  setDensity,
  globalFilter,
  setGlobalFilter,
  savedViews,
  activeView,
  onSaveView,
  onLoadView,
  onDeleteView,
  onResetFilters,
  viewModeToggle,
  columnLabels,
  extraToolbarSlot,
  searchPlaceholder,
}) {
  const allColumns = table
    .getAllLeafColumns()
    .filter((c) => c.id !== 'select' && c.id !== 'actions');
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const viewsPanelRef = useRef(null);
  const columnsPanelRef = useCallback((el) => keepInViewport(el), []);
  useEffect(() => {
    function onDoc(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const [viewName, setViewName] = useState('');

  // Surfaced on the Reset button so an active column filter is visible from the
  // toolbar too, not only from the column it sits in.
  const activeFilterCount = table
    .getState()
    .columnFilters.filter((f) => isActiveFilterValue(f.value)).length;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <input
        className="input"
        placeholder={searchPlaceholder || 'Search…'}
        value={globalFilter || ''}
        onChange={(e) => setGlobalFilter(e.target.value)}
        style={{ flex: '1 1 220px', maxWidth: 320, fontSize: 12, padding: '4px 8px', height: 30 }}
      />

      <details
        style={{ position: 'relative' }}
        onToggle={(e) => e.currentTarget.open && keepInViewport(viewsPanelRef.current)}
      >
        <summary
          className="btn btn-ghost btn-sm"
          style={{ cursor: 'pointer', listStyle: 'none', fontSize: 11 }}
        >
          ⭐ {activeView || 'Views'}
        </summary>
        <div
          ref={viewsPanelRef}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 30,
            marginTop: 4,
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 8,
            minWidth: 220,
            maxWidth: 'calc(100vw - 16px)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
            {Object.keys(savedViews).length === 0 && (
              <div style={{ color: 'var(--fg3)', fontSize: 11, padding: 4 }}>
                No saved views yet.
              </div>
            )}
            {Object.keys(savedViews).map((name) => (
              <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button
                  onClick={() => onLoadView(name)}
                  className="btn btn-ghost btn-sm"
                  style={{
                    flex: 1,
                    textAlign: 'left',
                    fontSize: 11,
                    ...(activeView === name
                      ? { background: 'var(--accent-bg)', color: 'var(--accent)' }
                      : {}),
                  }}
                >
                  {name}
                </button>
                <button
                  onClick={() => onDeleteView(name)}
                  className="btn btn-ghost btn-sm"
                  title="Delete view"
                  style={{ color: 'var(--danger)', fontSize: 11 }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div
            style={{ display: 'flex', gap: 4, borderTop: '1px solid var(--border)', paddingTop: 8 }}
          >
            <input
              className="input"
              placeholder="New view name…"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              style={{ flex: 1, fontSize: 11, padding: '3px 6px', height: 26 }}
            />
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                if (viewName.trim()) {
                  onSaveView(viewName.trim());
                  setViewName('');
                }
              }}
              style={{ fontSize: 11 }}
            >
              Save
            </button>
          </div>
        </div>
      </details>

      <button
        className="btn btn-ghost btn-sm"
        onClick={onResetFilters}
        title={
          activeFilterCount
            ? `${activeFilterCount} column filter${activeFilterCount === 1 ? '' : 's'} active — clear all filters and grouping`
            : 'Clear all filters and grouping'
        }
        style={{
          fontSize: 11,
          ...(activeFilterCount ? { color: 'var(--accent)', background: 'var(--accent-bg)' } : {}),
        }}
      >
        Reset{activeFilterCount ? ` (${activeFilterCount})` : ''}
      </button>

      <div ref={pickerRef} style={{ position: 'relative' }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setPickerOpen((o) => !o)}
          style={{ fontSize: 11 }}
        >
          Columns ({allColumns.filter((c) => c.getIsVisible()).length})
        </button>
        {pickerOpen && (
          <div
            ref={columnsPanelRef}
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              zIndex: 30,
              marginTop: 4,
              background: 'var(--bg2)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: 8,
              minWidth: 200,
              maxWidth: 'calc(100vw - 16px)',
              maxHeight: 320,
              overflow: 'auto',
              boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
            }}
          >
            {allColumns.map((c) => (
              <label
                key={c.id}
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  padding: '3px 4px',
                  fontSize: 11,
                  color: 'var(--fg2)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={c.getIsVisible()}
                  onChange={c.getToggleVisibilityHandler()}
                />
                <span>{columnLabels[c.id] || c.id}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          display: 'inline-flex',
          border: '1px solid var(--border)',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        {['compact', 'comfortable'].map((d) => (
          <button
            key={d}
            onClick={() => setDensity(d)}
            className="btn btn-ghost btn-sm"
            style={{
              fontSize: 11,
              borderRadius: 0,
              border: 0,
              ...(density === d ? { background: 'var(--accent-bg)', color: 'var(--accent)' } : {}),
            }}
          >
            {d}
          </button>
        ))}
      </div>

      {viewModeToggle && (
        <div
          style={{
            display: 'inline-flex',
            border: '1px solid var(--border)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {viewModeToggle.options.map((v) => (
            <button
              key={v.key}
              onClick={() => viewModeToggle.onChange(v.key)}
              className="btn btn-ghost btn-sm"
              style={{
                fontSize: 11,
                borderRadius: 0,
                border: 0,
                ...(viewModeToggle.current === v.key
                  ? { background: 'var(--accent-bg)', color: 'var(--accent)' }
                  : {}),
              }}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}

      {extraToolbarSlot}
    </div>
  );
}

/* ── Bulk action bar ───────────────────────────────────────────────────── */
function BulkActionBar({ selectedRows, bulkActions, onBulkUpdate, onClear }) {
  const ids = selectedRows.map((r) => r.original.id);
  const [openId, setOpenId] = useState(null);
  const [inputValue, setInputValue] = useState('');

  if (!ids.length) return null;

  const renderActionPanel = (action) => {
    if (action.kind === 'select' || action.kind === 'user') {
      const options = action.options || [];
      return (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            zIndex: 31,
            marginBottom: 4,
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 6,
            minWidth: 220,
            maxHeight: 240,
            overflow: 'auto',
            boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
          }}
        >
          {options.map((o) => (
            <button
              key={o.value}
              onClick={() => {
                onBulkUpdate(ids, {
                  [action.patchKey]: o.value,
                  ...(action.extraPatch?.(o) || {}),
                });
                setOpenId(null);
              }}
              className="btn btn-ghost btn-sm"
              style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 11 }}
            >
              {o.label}
            </button>
          ))}
          {options.length === 0 && (
            <div style={{ color: 'var(--fg3)', fontSize: 11, padding: 4 }}>No options</div>
          )}
        </div>
      );
    }
    if (action.kind === 'date') {
      return (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            zIndex: 31,
            marginBottom: 4,
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 8,
            minWidth: 220,
            boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <input
            className="input"
            type="date"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            style={{ fontSize: 11, padding: '4px 6px' }}
          />
          <button
            className="btn btn-ghost btn-sm"
            disabled={!inputValue}
            onClick={() => {
              onBulkUpdate(ids, {
                [action.patchKey]: inputValue,
                ...(action.extraPatch?.({ value: inputValue }) || {}),
              });
              setInputValue('');
              setOpenId(null);
            }}
            style={{ fontSize: 11 }}
          >
            Apply
          </button>
        </div>
      );
    }
    if (action.kind === 'text') {
      return (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            zIndex: 31,
            marginBottom: 4,
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 8,
            minWidth: 220,
            boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <input
            className="input"
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            style={{ fontSize: 11, padding: '4px 6px' }}
          />
          <button
            className="btn btn-ghost btn-sm"
            disabled={!inputValue.trim()}
            onClick={() => {
              const trimmed = inputValue.trim();
              onBulkUpdate(ids, {
                [action.patchKey]: trimmed,
                ...(action.extraPatch?.({ value: trimmed }) || {}),
              });
              setInputValue('');
              setOpenId(null);
            }}
            style={{ fontSize: 11 }}
          >
            Apply
          </button>
        </div>
      );
    }
    if (action.kind === 'action') {
      action.run?.(ids);
      setOpenId(null);
      return null;
    }
    return null;
  };

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 20,
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.3)',
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--fg2)', fontWeight: 600 }}>
        {ids.length} selected
      </span>
      {(bulkActions || []).map((action) => (
        <div key={action.id} style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              if (action.kind === 'action') {
                action.run?.(ids);
                return;
              }
              setOpenId(openId === action.id ? null : action.id);
              setInputValue('');
            }}
            style={{ fontSize: 11 }}
          >
            {action.label}
          </button>
          {openId === action.id && renderActionPanel(action)}
        </div>
      ))}
      <div style={{ flex: 1 }} />
      <button className="btn btn-ghost btn-sm" onClick={onClear} style={{ fontSize: 11 }}>
        Cancel
      </button>
    </div>
  );
}

function renderGroupLabel(value) {
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value.map((v) => (typeof v === 'object' ? (v.name ?? v.id) : v)).join(', ');
  }
  if (value && typeof value === 'object') return value.name ?? value.id ?? '—';
  return value || '—';
}

/**
 * Build one width spec per visible column for `computeFittedColumnWidths`.
 *
 * A column the consumer sized explicitly, or the user resized by hand, keeps
 * that width; everything else is flexible and shares out whatever the
 * container has left. `minSize` is what stops a column of buttons from being
 * squeezed to the point where its buttons are unreachable.
 *
 * `defsById` must be the *pre-TanStack* column definitions: TanStack merges its
 * own defaults (`size: 150`, `minSize: 20`, …) into every `column.columnDef`,
 * so reading sizes off the table instance cannot tell a column the consumer
 * sized apart from one it did not (#5043).
 */
function buildWidthSpecs(visibleColumns, defsById, userWidths) {
  return visibleColumns.map((column) => {
    const def = defsById[column.id] || {};
    const userWidth = userWidths[column.id];
    return {
      id: column.id,
      desired: userWidth ?? def.size ?? DEFAULT_COL_WIDTH,
      min: def.minSize ?? MIN_COL_WIDTH,
      // Only a declared maxSize caps the fit — see MAX_COL_WIDTH's note.
      max: def.maxSize,
      flexible: userWidth == null && def.size == null,
    };
  });
}

// The table lays out with `table-layout: fixed`, so a body cell is already
// bounded by its column's fitted width. This wrapper is what turns that bound
// into an ellipsis rather than a hard clip, and carries the full value in a
// native `title` so a truncated cell is still readable on hover. Dragging the
// column's resize handle (in the header) is the durable way to widen it.
function TruncatedCell({ cell }) {
  const rawValue = cell.getValue?.();
  const titleText =
    typeof rawValue === 'string' || typeof rawValue === 'number' ? String(rawValue) : undefined;
  return (
    <div
      title={titleText}
      style={{
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </div>
  );
}

/* ── Stacked rows (opt-in narrow-screen mode) ──────────────────────────── */
// Rendered in place of the table when a consumer passes `stackedBelow` and the
// measured container is narrower than it. One block per row, one "label /
// value" pair per visible column, values wrap instead of truncating, and every
// interactive part has a 44px touch target. Sort, search and group-by stay in
// the toolbar; the per-column filter row is table-only, so active filters are
// named in a notice above the rows. Not virtualized: every row is mounted.
const TOUCH_TARGET = 44;

function StackedRows({
  rows,
  columnLabels,
  onRowClick,
  emptyMessage,
  filteredColumnIds,
  onClearColumnFilters,
}) {
  // A column filter set at a wider width still hides rows here, and the filter
  // row that shows it is table-only — so name it, and offer the way out.
  const filterNotice =
    filteredColumnIds.size > 0 ? (
      <div
        style={{
          padding: '10px 12px',
          fontSize: 11,
          borderBottom: '1px solid var(--border)',
          background: 'var(--accent-bg)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ color: 'var(--fg2)' }}>
          Column filters on:{' '}
          <span style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
            {Array.from(filteredColumnIds)
              .map((id) => columnLabels[id] || id)
              .join(', ')}
          </span>
        </span>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onClearColumnFilters}
          style={{ minHeight: TOUCH_TARGET, fontSize: 11, color: 'var(--accent)' }}
        >
          Clear column filters
        </button>
      </div>
    ) : null;
  if (rows.length === 0) {
    return (
      <div>
        {filterNotice}
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg3)', fontStyle: 'italic' }}>
          {emptyMessage}
        </div>
      </div>
    );
  }
  return (
    <div>
      {filterNotice}
      <div role="list" data-testid="workbench-stacked-rows">
        {rows.map((row) => {
          if (row.getIsGrouped()) {
            const groupCol = row.groupingColumnId;
            return (
              <div key={row.id} role="listitem">
                <button
                  type="button"
                  aria-expanded={row.getIsExpanded()}
                  onClick={() => row.toggleExpanded()}
                  style={{
                    display: 'block',
                    width: '100%',
                    minHeight: TOUCH_TARGET,
                    padding: '10px 12px',
                    textAlign: 'left',
                    font: 'inherit',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--fg)',
                    background: 'var(--bg3)',
                    border: 0,
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ marginRight: 6, color: 'var(--fg3)' }}>
                    {row.getIsExpanded() ? '▼' : '▶'}
                  </span>
                  <span
                    style={{ color: 'var(--fg3)', fontFamily: 'var(--font-mono)', fontSize: 10 }}
                  >
                    {columnLabels[groupCol] || groupCol}:{' '}
                  </span>
                  {renderGroupLabel(row.getGroupingValue(groupCol))} ({row.subRows.length})
                </button>
              </div>
            );
          }
          return (
            <div
              key={row.id}
              role="listitem"
              tabIndex={onRowClick ? 0 : undefined}
              onClick={() => onRowClick?.(row)}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              style={{
                minHeight: TOUCH_TARGET,
                padding: '10px 12px',
                borderBottom: '1px solid var(--border)',
                background: row.getIsSelected() ? 'var(--accent-bg)' : 'transparent',
                cursor: onRowClick ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                minWidth: 0,
              }}
            >
              {row.getVisibleCells().map((cell) => {
                const label = columnLabels[cell.column.id];
                return (
                  <div
                    key={cell.id}
                    style={{
                      display: 'flex',
                      flexDirection: label ? 'column' : 'row',
                      gap: 2,
                      minWidth: 0,
                      minHeight: label ? undefined : TOUCH_TARGET,
                      alignItems: label ? undefined : 'center',
                      fontSize: 12,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {label && (
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 10,
                          color: 'var(--fg3)',
                        }}
                      >
                        {label}
                      </span>
                    )}
                    <div style={{ minWidth: 0 }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Main component ────────────────────────────────────────────────────── */
function Workbench({
  rows,
  columns: rawColumns,
  storageKey,
  session,
  onRowClick,
  onBulkUpdate,
  bulkActions,
  enableSelection = true,
  viewModeToggle,
  emptyMessage = 'No rows match the current filters.',
  searchPlaceholder = 'Search…',
  extraToolbarSlot,
  filterRequest,
  attentionSignal,
  // Bump when a column's `defaultVisible` changes deliberately, so the new
  // default reaches users who already have this register's visibility stored.
  // See the note beside `columnDefaultsChanged` below.
  columnsVersion = null,
  // Opt-in narrow-screen mode: a width in px. While the list's own container is
  // narrower than this, rows render as stacked blocks (StackedRows) instead of
  // a table. Unset (the default) = the table at every width, unchanged.
  stackedBelow = null,
}) {
  const userId = session?.userId || 'anon';
  const prefsKey = PREFS_KEY(storageKey, userId);
  const initial = useMemo(() => loadPrefs(prefsKey), [prefsKey]);

  const defaultVisibility = useMemo(() => {
    const m = {};
    for (const c of rawColumns) m[c.id] = c.defaultVisible !== false;
    if (enableSelection) m.select = true;
    return m;
  }, [rawColumns, enableSelection]);

  // Changing a column's `defaultVisible` does nothing for anyone who has
  // already opened the register, and that is not obvious from the code that
  // sets it (GH 6741).
  //
  // `defaultVisibility` feeds `useState` as an INITIALISER only, and the effect
  // below persists the whole resolved visibility map on mount. So the first
  // time a user opens a register, every column's current visibility is frozen
  // into their localStorage — and every later change to a default is overridden
  // by that stored map, silently, for exactly the people who use the product
  // most. A layout change shipped this way passes review, passes CI, and
  // changes nothing on any real screen.
  //
  // `columnsVersion` is how a register says "this default has deliberately
  // changed; discard the stored visibility once". Scoped to visibility alone:
  // bumping the global PREFS_VERSION would also throw away saved views, filters,
  // sorting and column widths across every register in the product, which is a
  // large amount of other people's work to destroy in order to hide one column.
  const storedColumnsVersion = initial.columnsVersion ?? null;
  const columnDefaultsChanged = columnsVersion != null && storedColumnsVersion !== columnsVersion;

  const columnLabels = useMemo(() => {
    const m = {};
    for (const c of rawColumns) m[c.id] = c.label;
    return m;
  }, [rawColumns]);

  const [density, setDensity] = useState(initial.density || 'comfortable');
  const [sorting, setSorting] = useState(initial.sorting || []);
  const [columnFilters, setColumnFilters] = useState(initial.columnFilters || []);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState(
    columnDefaultsChanged
      ? defaultVisibility
      : { ...defaultVisibility, ...(initial.columnVisibility || {}) }
  );
  const [grouping, setGrouping] = useState(initial.grouping || []);
  // A grouped register renders group headers whose rows only exist in the DOM
  // once the group is expanded. `grouping` is persisted (see savePrefs below)
  // but the expansion state is not, so a register the user had grouped came
  // back from a reload with EVERY group collapsed and not one data row
  // rendered — a register that looks empty, with the group-by chip as the only
  // clue why, and no row-level action reachable at all. Restore expanded when
  // the grouping we are restoring came from prefs (`true` is TanStack's
  // all-rows-expanded value; the first manual toggle replaces it with the
  // usual per-row map). Grouping applied interactively still collapses, which
  // is the point of the gesture and has Expand All next to it (#5063).
  const [expanded, setExpanded] = useState(() => ((initial.grouping || []).length ? true : {}));
  const [rowSelection, setRowSelection] = useState({});
  const [savedViews, setSavedViews] = useState(initial.savedViews || {});
  const [activeView, setActiveView] = useState(initial.activeView || null);
  const [columnSizing, setColumnSizing] = useState(initial.columnSizing || {});

  useEffect(() => {
    savePrefs(prefsKey, {
      density,
      sorting,
      columnFilters,
      columnVisibility,
      // Stamped so the discard above happens exactly once per bump, rather than
      // on every mount for the life of the register.
      columnsVersion: columnsVersion ?? storedColumnsVersion ?? null,
      grouping,
      savedViews,
      activeView,
      columnSizing,
    });
  }, [
    prefsKey,
    columnsVersion,
    storedColumnsVersion,
    density,
    sorting,
    columnFilters,
    columnVisibility,
    grouping,
    savedViews,
    activeView,
    columnSizing,
  ]);

  const tanstackColumns = useMemo(() => {
    const cols = [];
    if (enableSelection) {
      cols.push({
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            aria-label="Select all rows"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label="Select row"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        enableGrouping: false,
        enableResizing: false,
        size: SELECT_COL_WIDTH,
        minSize: SELECT_COL_WIDTH,
        maxSize: SELECT_COL_WIDTH,
      });
    }
    for (const def of rawColumns) cols.push(adaptColumn(def, density));
    return cols;
  }, [rawColumns, density, enableSelection]);

  // Consumer-driven filter application (e.g. "show all controls with this
  // issue" from a detail pop-up). Additive: replaces only the requested
  // column's filter and makes that column visible; everything else —
  // including the user's other filters — is left untouched. Re-applies when
  // the request's nonce changes.
  useEffect(() => {
    if (!filterRequest?.id) return;
    setColumnFilters((prev) => [
      ...prev.filter((f) => f.id !== filterRequest.id),
      { id: filterRequest.id, value: filterRequest.value },
    ]);
    setColumnVisibility((prev) => ({ ...prev, [filterRequest.id]: true }));
  }, [filterRequest]);

  // A filtering column is marked permanently — tinted header, funnel badge,
  // tinted filter cell — so a narrowed list is never mistaken for an empty
  // one. `attentionSignal` is the other half: a consumer passes whatever
  // filter it owns OUTSIDE the table (a status chip, an applicability
  // toggle), and every already-filtered column pulses when that changes.
  // That is the exact moment a user blames the wrong control for an empty
  // list, having forgotten the column filter they set minutes earlier.
  const filteredColumnIds = useMemo(
    () => new Set(columnFilters.filter((f) => isActiveFilterValue(f.value)).map((f) => f.id)),
    [columnFilters]
  );
  const [bumpNonce, setBumpNonce] = useState(0);
  const lastAttentionSignal = useRef(attentionSignal);
  useEffect(() => {
    if (lastAttentionSignal.current === attentionSignal) return;
    lastAttentionSignal.current = attentionSignal;
    if (filteredColumnIds.size === 0) return;
    setBumpNonce((n) => n + 1);
  }, [attentionSignal, filteredColumnIds]);
  // Alternating class names restart the CSS animation without remounting the
  // header cell — a remount would drop the drag handle mid-gesture.
  const bumpClass = bumpNonce === 0 ? '' : bumpNonce % 2 ? 'wb-filter-bump-a' : 'wb-filter-bump-b';

  // Base order (see orderRowsNewestFirst): newest-first when the rows carry a
  // creation timestamp, otherwise exactly what the consumer passed. TanStack's
  // own sorting is layered on top of this, so a user-chosen sort still wins.
  const orderedRows = useMemo(() => orderRowsNewestFirst(rows), [rows]);

  const table = useReactTable({
    data: orderedRows,
    columns: tanstackColumns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      grouping,
      expanded,
      rowSelection,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onGroupingChange: setGrouping,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    enableGrouping: true,
    // Column widths are owned by this component, not TanStack: they are fitted
    // to the measured container (see the `fittedWidths` memo below) and applied
    // through a <colgroup>. `columnSizing` here is only the user's explicit
    // resize overrides, which feed that fit as fixed desired widths.
    enableColumnResizing: false,
    autoResetExpanded: false,
    globalFilterFn: 'includesString',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row, relativeIndex) => (row.id != null ? String(row.id) : String(relativeIndex)),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || over.id !== 'group-by-zone') return;
      const colId = active.data?.current?.columnId;
      if (!colId || grouping.includes(colId)) return;
      const col = tanstackColumns.find((c) => c.id === colId);
      if (!col?.enableGrouping) return;
      setGrouping((g) => [...g, colId]);
    },
    [grouping, tanstackColumns]
  );

  const onSaveView = (name) => {
    const view = { density, sorting, columnFilters, columnVisibility, grouping, columnSizing };
    setSavedViews((v) => ({ ...v, [name]: view }));
    setActiveView(name);
  };
  const onLoadView = (name) => {
    const v = savedViews[name];
    if (!v) return;
    setDensity(v.density || 'comfortable');
    setSorting(v.sorting || []);
    setColumnFilters(v.columnFilters || []);
    setColumnVisibility({ ...defaultVisibility, ...(v.columnVisibility || {}) });
    setGrouping(v.grouping || []);
    // Same reason as the initial state above: a saved view that carries
    // grouping must come back showing its rows, not as a list of collapsed
    // headers the user has to expand before the view they saved appears.
    setExpanded((v.grouping || []).length ? true : {});
    setColumnSizing(v.columnSizing || {});
    setActiveView(name);
  };
  const onDeleteView = (name) => {
    setSavedViews((v) => {
      const next = { ...v };
      delete next[name];
      return next;
    });
    if (activeView === name) setActiveView(null);
  };
  const onResetFilters = () => {
    setColumnFilters([]);
    setGlobalFilter('');
    setGrouping([]);
    setSorting([]);
    setActiveView(null);
  };

  const cellPad = density === 'compact' ? '4px 8px' : '8px 10px';
  const headerPad = density === 'compact' ? '6px 8px' : '8px 10px';
  const rowH = density === 'compact' ? 32 : 44;
  const selectedRows = table.getSelectedRowModel().rows;

  // Virtualization — only when no grouping is active (grouped views have few
  // top-level rows; virtualizing mixed group+leaf rows is unnecessary).
  const VIRTUAL_THRESHOLD = 50;
  const tableContainerRef = useRef(null);
  const tableRows = table.getRowModel().rows;
  const useVirtual = grouping.length === 0 && tableRows.length >= VIRTUAL_THRESHOLD;

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => rowH,
    overscan: 10,
  });

  const virtualItems = useVirtual ? rowVirtualizer.getVirtualItems() : null;
  const totalVirtualSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems?.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems?.length > 0 ? totalVirtualSize - virtualItems[virtualItems.length - 1].end : 0;
  const visibleColumns = table.getVisibleLeafColumns();
  const visibleColCount = visibleColumns.length;

  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;
    const measure = () => {
      // clientWidth excludes the border and any vertical scrollbar, which is
      // exactly the space the columns get to share.
      setContainerWidth((prev) => (prev === el.clientWidth ? prev : el.clientWidth));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const stacked = stackedBelow != null && containerWidth > 0 && containerWidth < stackedBelow;

  /* ── Fit the columns to the container ────────────────────────────────── */
  // The table must never render wider than the space it has been given —
  // otherwise the rightmost column (typically the row actions) disappears
  // behind a horizontal scrollbar. So we measure the container and hand every
  // visible column an explicit width that adds up to it, applied through the
  // <colgroup> below with `table-layout: fixed`.
  // ── Reserve the sticky header's height for programmatic scrolling ─────
  // The <thead> below is `position: sticky; top: 0` inside this scroll
  // container, and it carries TWO rows — the column headers and the column
  // filters. Once the container actually scrolls (the virtualized path gives
  // it a maxHeight; without one it never scrolls and the header never
  // overlays anything), anything scrolled flush to the top of the container
  // lands UNDERNEATH that header.
  //
  // That is what `scrollIntoView` / `scrollIntoViewIfNeeded` does by default,
  // so a row brought into view programmatically — by keyboard focus, by an
  // anchor, or by an automated click — is parked under the header, and the
  // click that follows lands on a column header (which toggles the sort,
  // silently reordering the list under the user) or in a column-filter input.
  // Reported as the shared-Workbench half of #5041.
  //
  // scroll-padding-top tells the browser to stop short of the header on every
  // programmatic scroll into this container. Measured rather than hardcoded
  // because the header's height moves with density and the column set.
  const theadRef = useRef(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    const el = theadRef.current;
    if (!el) return undefined;
    const measure = () => {
      const h = el.offsetHeight || 0;
      setStickyHeaderHeight((prev) => (prev === h ? prev : h));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [density, visibleColCount, stacked]);

  const columnDefsById = useMemo(() => {
    const m = {};
    for (const c of tanstackColumns) m[c.id] = c;
    return m;
  }, [tanstackColumns]);

  const visibleColumnKey = visibleColumns.map((c) => c.id).join('|');
  const widthSpecs = useMemo(
    () => buildWidthSpecs(visibleColumns, columnDefsById, columnSizing),
    // `visibleColumns` is a fresh array on every render; what actually changes
    // the fit is which columns are visible and how each one is sized.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleColumnKey, columnDefsById, columnSizing]
  );
  const fittedWidths = useMemo(
    () => computeFittedColumnWidths(widthSpecs, containerWidth),
    [widthSpecs, containerWidth]
  );
  const fittedTotal = useMemo(
    () => Object.values(fittedWidths).reduce((sum, w) => sum + w, 0),
    [fittedWidths]
  );
  // Only when the columns' minimums genuinely cannot fit does the table exceed
  // its container and the horizontal scrollbar come back — at that point it is
  // the only honest option left.
  const overflows = containerWidth > 0 && fittedTotal > containerWidth;

  /* ── Manual column resize ────────────────────────────────────────────── */
  // Hand-rolled rather than TanStack's handler because the drag has to start
  // from the width the column is *rendered* at (its fitted width), not from the
  // width it would have had unfitted — otherwise grabbing the handle makes the
  // column jump before it moves.
  const [resizingId, setResizingId] = useState(null);
  const endResizeRef = useRef(null);
  useEffect(() => () => endResizeRef.current?.(), []);

  const startResize = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();
      endResizeRef.current?.();
      const pointX = (e) => (e.touches ? e.touches[0]?.clientX : e.clientX) ?? 0;
      const startX = pointX(event.nativeEvent || event);
      const def = columnDefsById[column.id] || {};
      const startWidth = fittedWidths[column.id] ?? def.size ?? DEFAULT_COL_WIDTH;
      const min = def.minSize ?? MIN_COL_WIDTH;
      const max = def.maxSize ?? MAX_COL_WIDTH;

      const onMove = (e) => {
        const next = Math.round(Math.min(max, Math.max(min, startWidth + (pointX(e) - startX))));
        setColumnSizing((prev) =>
          prev[column.id] === next ? prev : { ...prev, [column.id]: next }
        );
      };
      const onEnd = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onEnd);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onEnd);
        endResizeRef.current = null;
        setResizingId(null);
      };
      endResizeRef.current = onEnd;
      setResizingId(column.id);
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onEnd);
    },
    [fittedWidths, columnDefsById]
  );

  // Double-click hands the column back to the automatic fit.
  const resetColumnWidth = useCallback((columnId) => {
    setColumnSizing((prev) => {
      if (prev[columnId] == null) return prev;
      const next = { ...prev };
      delete next[columnId];
      return next;
    });
  }, []);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      {/* minWidth:0 stops a wide table from pushing this column out past the
          page shell — the table scrolls inside its own box instead. */}
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, maxWidth: '100%' }}
      >
        <Toolbar
          table={table}
          density={density}
          setDensity={setDensity}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          savedViews={savedViews}
          activeView={activeView}
          onSaveView={onSaveView}
          onLoadView={onLoadView}
          onDeleteView={onDeleteView}
          onResetFilters={onResetFilters}
          viewModeToggle={viewModeToggle}
          columnLabels={columnLabels}
          extraToolbarSlot={extraToolbarSlot}
          searchPlaceholder={searchPlaceholder}
        />
        <GroupByZone
          grouping={grouping}
          onChange={setGrouping}
          columns={table.getAllLeafColumns()}
          columnLabels={columnLabels}
          onExpandAll={() => table.toggleAllRowsExpanded(true)}
          onCollapseAll={() => table.toggleAllRowsExpanded(false)}
        />

        <div
          ref={tableContainerRef}
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'auto',
            background: 'var(--bg2)',
            maxHeight: useVirtual ? 'calc(100vh - 260px)' : undefined,
            // See the note on stickyHeaderHeight above — keeps a programmatic
            // scroll from parking a row under the sticky header (#5041).
            scrollPaddingTop: stickyHeaderHeight || undefined,
          }}
        >
          {stacked ? (
            <StackedRows
              rows={tableRows}
              columnLabels={columnLabels}
              onRowClick={onRowClick}
              emptyMessage={emptyMessage}
              filteredColumnIds={filteredColumnIds}
              onClearColumnFilters={() => setColumnFilters([])}
            />
          ) : (
            <table
              style={{
                // Fixed layout is what makes the fitted widths authoritative:
                // without it the browser re-derives columns from cell content and
                // the table drifts back outside the container.
                tableLayout: 'fixed',
                width: overflows ? fittedTotal : '100%',
                borderCollapse: 'collapse',
                fontSize: 12,
              }}
            >
              <colgroup>
                {visibleColumns.map((c) => (
                  <col key={c.id} style={{ width: fittedWidths[c.id] }} />
                ))}
              </colgroup>
              <thead
                ref={theadRef}
                style={{ background: 'var(--bg3)', position: 'sticky', top: 0, zIndex: 1 }}
              >
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((h) => {
                      const sortDir = h.column.getIsSorted();
                      const canResize = columnDefsById[h.column.id]?.enableResizing !== false;
                      const headerLabel = columnLabels[h.column.id] || h.column.id;
                      const isFiltered = filteredColumnIds.has(h.column.id);
                      return (
                        <th
                          key={h.id}
                          className={isFiltered ? bumpClass : undefined}
                          onClick={() => {
                            if (h.column.getCanSort()) h.column.toggleSorting();
                          }}
                          style={{
                            position: 'relative',
                            padding: headerPad,
                            textAlign: 'left',
                            borderBottom: '1px solid var(--border)',
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            color: isFiltered ? 'var(--accent)' : 'var(--fg3)',
                            background: isFiltered ? 'var(--accent-bg)' : undefined,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            cursor: h.column.getCanSort() ? 'pointer' : 'default',
                            userSelect: 'none',
                          }}
                        >
                          <DraggableHeader column={h.column}>
                            {/* The label truncates; the sort arrow never does, so
                              a narrowed column still shows how it is sorted. */}
                            <span
                              title={headerLabel}
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {flexRender(h.column.columnDef.header, h.getContext())}
                            </span>
                            {sortDir === 'asc' && <span style={{ flex: '0 0 auto' }}>▲</span>}
                            {sortDir === 'desc' && <span style={{ flex: '0 0 auto' }}>▼</span>}
                            {/* Never truncates, same reason as the sort arrow: a
                              narrowed column still has to admit it is filtering. */}
                            {isFiltered && (
                              <span
                                role="button"
                                tabIndex={0}
                                aria-label={`Clear the ${headerLabel} filter`}
                                data-filtered-column={h.column.id}
                                title={`Filtered: ${describeFilterValue(
                                  h.column.getFilterValue(),
                                  h.column.columnDef.meta
                                )} — click to clear this column's filter`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  h.column.setFilterValue(undefined);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') return;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  h.column.setFilterValue(undefined);
                                }}
                                style={{
                                  flex: '0 0 auto',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  color: 'var(--accent)',
                                }}
                              >
                                <FilterFunnelIcon />
                              </span>
                            )}
                          </DraggableHeader>
                          {canResize && (
                            <div
                              onMouseDown={(e) => startResize(e, h.column)}
                              onTouchStart={(e) => startResize(e, h.column)}
                              onClick={(e) => e.stopPropagation()}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                resetColumnWidth(h.column.id);
                              }}
                              title="Drag to resize · double-click to reset"
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                height: '100%',
                                width: 6,
                                cursor: 'col-resize',
                                userSelect: 'none',
                                touchAction: 'none',
                                background:
                                  resizingId === h.column.id ? 'var(--accent)' : 'transparent',
                              }}
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
                <tr>
                  {visibleColumns.map((c) => {
                    const isFiltered = filteredColumnIds.has(c.id);
                    return (
                      <th
                        key={c.id}
                        className={isFiltered ? bumpClass : undefined}
                        style={{
                          padding: '4px 6px',
                          borderBottom: '1px solid var(--border)',
                          background: isFiltered ? 'var(--accent-bg)' : undefined,
                        }}
                      >
                        <ColumnFilter column={c} rows={rows} />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {virtualItems ? (
                  /* Virtualized path — no grouping, large dataset */
                  <>
                    {paddingTop > 0 && (
                      <tr>
                        <td
                          colSpan={visibleColCount}
                          style={{ height: paddingTop, padding: 0, border: 0 }}
                        />
                      </tr>
                    )}
                    {virtualItems.map((virtualRow) => {
                      const row = tableRows[virtualRow.index];
                      return (
                        <tr
                          key={row.id}
                          data-index={virtualRow.index}
                          onClick={() => onRowClick?.(row)}
                          style={{
                            cursor: onRowClick ? 'pointer' : 'default',
                            height: rowH,
                            background: row.getIsSelected() ? 'var(--accent-bg)' : 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            if (!row.getIsSelected())
                              e.currentTarget.style.background = 'var(--bg3)';
                          }}
                          onMouseLeave={(e) => {
                            if (!row.getIsSelected())
                              e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              style={{ padding: cellPad, borderBottom: '1px solid var(--border)' }}
                            >
                              <TruncatedCell cell={cell} />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {paddingBottom > 0 && (
                      <tr>
                        <td
                          colSpan={visibleColCount}
                          style={{ height: paddingBottom, padding: 0, border: 0 }}
                        />
                      </tr>
                    )}
                  </>
                ) : (
                  /* Non-virtualized path — grouping active or small dataset */
                  <>
                    {tableRows.map((row) => {
                      if (row.getIsGrouped()) {
                        const groupCol = row.groupingColumnId;
                        const value = row.getGroupingValue(groupCol);
                        const label = renderGroupLabel(value);
                        return (
                          <tr
                            key={row.id}
                            onClick={() => row.toggleExpanded()}
                            style={{ cursor: 'pointer', background: 'var(--bg3)' }}
                          >
                            <td
                              colSpan={row.getVisibleCells().length}
                              style={{
                                padding: cellPad,
                                fontSize: 12,
                                fontWeight: 600,
                                color: 'var(--fg)',
                                borderBottom: '1px solid var(--border)',
                              }}
                            >
                              <span style={{ marginRight: 6, color: 'var(--fg3)' }}>
                                {row.getIsExpanded() ? '▼' : '▶'}
                              </span>
                              <span
                                style={{
                                  color: 'var(--fg3)',
                                  fontSize: 10,
                                  marginRight: 8,
                                  fontFamily: 'var(--font-mono)',
                                }}
                              >
                                {columnLabels[groupCol] || groupCol}:
                              </span>
                              {label}
                              <span
                                style={{
                                  marginLeft: 8,
                                  fontSize: 10,
                                  fontFamily: 'var(--font-mono)',
                                  color: 'var(--fg3)',
                                  background: 'var(--bg2)',
                                  padding: '2px 8px',
                                  borderRadius: 99,
                                }}
                              >
                                {row.subRows.length}
                              </span>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr
                          key={row.id}
                          onClick={() => onRowClick?.(row)}
                          style={{
                            cursor: onRowClick ? 'pointer' : 'default',
                            height: rowH,
                            background: row.getIsSelected() ? 'var(--accent-bg)' : 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            if (!row.getIsSelected())
                              e.currentTarget.style.background = 'var(--bg3)';
                          }}
                          onMouseLeave={(e) => {
                            if (!row.getIsSelected())
                              e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              style={{ padding: cellPad, borderBottom: '1px solid var(--border)' }}
                            >
                              <TruncatedCell cell={cell} />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {tableRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={table.getAllLeafColumns().length}
                          style={{
                            padding: 32,
                            textAlign: 'center',
                            color: 'var(--fg3)',
                            fontStyle: 'italic',
                          }}
                        >
                          <div>{emptyMessage}</div>
                          {/* The list is empty AND a column filter is on: name it
                            here rather than making the user hunt the header row. */}
                          {filteredColumnIds.size > 0 && (
                            <div style={{ marginTop: 10, fontStyle: 'normal', fontSize: 11 }}>
                              <span style={{ color: 'var(--fg2)' }}>Column filters still on: </span>
                              <span
                                style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}
                              >
                                {Array.from(filteredColumnIds)
                                  .map((id) => columnLabels[id] || id)
                                  .join(', ')}
                              </span>
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setColumnFilters([])}
                                style={{ marginLeft: 10, fontSize: 11, color: 'var(--accent)' }}
                              >
                                Clear column filters
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          )}
        </div>

        <BulkActionBar
          selectedRows={selectedRows}
          bulkActions={bulkActions}
          onBulkUpdate={(ids, patch) => {
            onBulkUpdate?.(ids, patch);
            setRowSelection({});
          }}
          onClear={() => setRowSelection({})}
        />
      </div>
    </DndContext>
  );
}

export default Workbench;
